/**
 * V2 — Modèle de tâches dérivées (zéro migration DB).
 *
 * Une tâche n'est PAS persistée. Elle est calculée à la volée à partir de
 * l'état d'un bien (statut, dates, drapeaux), de la même façon que
 * `dashboardViews.ts` dérive ses tuiles. Cette approche permet à la V2 de
 * proposer une inbox priorisée pour chaque rôle sans toucher au schéma.
 *
 * Quand un user "résout" une tâche, on patch le bien concerné (le bon champ
 * disparaît du predicate → la tâche disparaît automatiquement à la prochaine
 * invalidation React Query).
 */

import type { BienRow } from "@/schemas/bien";
import { today } from "@/lib/today";
import { isPebExpiringSoon } from "@/lib/pebCycle";
import { computeStatutEls } from "@/lib/elsStatus";
import type { Bien } from "@/types/bien";
import type { AppRole } from "@/contexts/RoleContext";
import { isCLevelReady, isCycleReady, isVisibleForRole } from "./bienLifecycle";
import { getCycleTodoFields, isElsLance } from "@/lib/bienPrepValidation";
import { getMailingCompleteness } from "./dashboardBuckets";
import { hasUnpublishedChanges } from "@/lib/publicationHelpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TaskKind =
  // Collaborateur
  | "els-no-date"
  | "els-date-soon"
  | "els-overdue-cloture"
  | "els-cloture-pending-final"
  | "transfert-elec-todo"
  | "photos-pret-todo"
  // Publication
  | "publication-photos-missing"
  | "publication-video-missing"
  | "publication-texte-missing"
  | "publication-stale"
  | "publication-ready-to-push"
  // C-Level
  | "dispo-long-1m"
  | "dispo-long-3m"
  | "dispo-long-6m"
  | "peb-absent-active"
  | "peb-expiring"
  | "mailing-incomplete-active"
  | "clevel-fields-missing"
  | "cycle-fields-missing"
  // Prestataire
  | "mission-els-soon"
  | "mission-els-today"
  | "mission-peb-soon"
  | "mission-peb-today";

export type TaskPriority = "urgent" | "high" | "medium" | "low";

export interface Task {
  id: string;                // `${bien.id}:${kind}`
  kind: TaskKind;
  bien_id: string;
  reference: string;
  adresse: string;
  commune: string;
  priority: TaskPriority;
  /** Clé i18n du libellé court (à résoudre via `t(titleKey, params)`). */
  titleKey: string;
  /** Clé i18n de la ligne secondaire = copie traduisible (à résoudre via `t(hintKey, params)`). */
  hintKey?: string;
  // Frontière hint : `hintRaw` = le hint entier est une donnée non traduisible (ex. une heure d'horloge) ;
  // `hintKey` = copie traduisible ; pour une donnée enveloppée dans du texte traduisible, utiliser
  // `hintKey` + `params` (ex. `v2.tasks.atTime.hint` "à {{time}}").
  /** Ligne secondaire brute = donnée non traduisible (ex. heure d'horloge). */
  hintRaw?: string;
  /** Paramètres d'interpolation/pluralisation pour titleKey/hintKey. */
  params?: Record<string, unknown>;
  /** Route V2 à ouvrir quand on clique sur la tâche. */
  href: string;
  /** Quel collaborateur est responsable (si applicable). */
  ownerId?: string | null;
  /** Tri secondaire : plus c'est petit, plus c'est urgent (jour 0 = aujourd'hui). */
  daysWeight: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PRIO_WEIGHT: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const isDispo = (b: BienRow) =>
  (b.disponibilite || "").toUpperCase() === "DISPONIBLE";
/** Un bien "actif" en V2 = dispo, ou pas encore loué/vendu. */
const isActive = (b: BienRow) => {
  const d = (b.disponibilite || "").toUpperCase();
  return d !== "LOUÉ" && d !== "VENDU";
};

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = today();
  const ms = d.getTime() - now.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function daysSinceDispo(b: BienRow): number | null {
  if (!isDispo(b) || !b.seraDispoPour) return null;
  const d = new Date(b.seraDispoPour);
  if (isNaN(d.getTime())) return null;
  const ms = today().getTime() - d.getTime();
  return ms >= 0 ? Math.floor(ms / (24 * 60 * 60 * 1000)) : null;
}

const ref = (b: BienRow) => b.referenceInterne || b.reference || "—";
const adresse = (b: BienRow) => b.adresse || "—";
const commune = (b: BienRow) => b.ville || "—";

/**
 * URL d'édition d'un bien en V2. La route /v2/biens/:id monte la fiche V1
 * complète (validation, bannières, autosave) à l'intérieur du layout V2 —
 * l'utilisateur garde la sidebar V2 et son contexte de navigation. Le
 * `section` est conservé pour compat (ignoré par V1 BienDetailPage si non
 * supporté).
 */
function bienHref(b: BienRow, section?: string): string {
  return section ? `/v2/biens/${b.id}/${section}` : `/v2/biens/${b.id}`;
}

// ---------------------------------------------------------------------------
// Task derivation per role
// ---------------------------------------------------------------------------

interface DeriveCtx {
  collabId?: string | null;
  prestataireId?: string | null;
}

function deriveCollabTasks(biens: BienRow[], ctx: DeriveCtx): Task[] {
  const out: Task[] = [];
  // Garde-fou métier V2 : identité complète (`isVisibleForRole`) ET un ELS
  // réellement lancé avec son cycle complet (collaborateur désigné). Depuis
  // le plan « deux cycles » (2026-09-15) l'identité seule ne suffit plus : un
  // bien importé, complet pour la publication mais sans aucun cycle ouvert,
  // générait sinon « Planifier l'ELS » pour un ELS que personne n'a décidé.
  const readyForCollab = biens.filter(
    (b) => isVisibleForRole(b, "collaborateur") && isElsLance(b as Record<string, unknown>) && isCycleReady(b).ok,
  );
  const myBiens = ctx.collabId
    ? readyForCollab.filter((b) => b.collaborateurSrs === ctx.collabId)
    : readyForCollab;

  for (const b of myBiens) {
    // ELS sans date alors que bien actif → à planifier.
    // `statutEls` retiré (mig. 112) : la colonne était vide partout, donc
    // `!b.statutEls` valait toujours vrai. Comportement inchangé.
    if (isActive(b) && !b.edlDatePrevue) {
      out.push({
        id: `${b.id}:els-no-date`,
        kind: "els-no-date",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.planifierEls.title",
        hintKey: "v2.tasks.planifierEls.hint",
        href: bienHref(b, "els"),
        ownerId: b.collaborateurSrs,
        daysWeight: -1,
      });
    }

    // ELS prévue dans <7j
    const dEls = daysUntil(b.edlDatePrevue);
    if (dEls !== null && dEls >= 0 && dEls <= 7 && !b.dossierCloture) {
      out.push({
        id: `${b.id}:els-date-soon`,
        kind: "els-date-soon",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: dEls <= 2 ? "urgent" : "high",
        titleKey: dEls === 0 ? "v2.tasks.elsAujourdhui.title" : "v2.tasks.elsDansJours.title",
        hintKey: b.edlHeure ? "v2.tasks.atTime.hint" : undefined,
        params: { days: dEls, time: b.edlHeure ?? undefined },
        href: bienHref(b, "els"),
        ownerId: b.collaborateurSrs,
        daysWeight: dEls,
      });
    }

    // ELS passée mais dossier pas clôturé (côté collab)
    // ⚠️ Cette tâche ne s'est JAMAIS déclenchée. Elle était gardée par
    // `statutEls`, colonne vide sur les 1574 biens depuis la disparition de
    // son écrivain en juillet : la condition était fausse par construction.
    // Mesuré le 2026-08-10 : 4 biens en location auraient dû la voir.
    // On lit désormais le statut CALCULÉ, comme `useElsAlertCounts`.
    const els = computeStatutEls(b as unknown as Bien);
    const elsDone = els === "els_realise" || els === "transfert_fait";
    if (elsDone && !b.dossierCloture && !b.dossierClos) {
      out.push({
        id: `${b.id}:els-overdue-cloture`,
        kind: "els-overdue-cloture",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.cloturerEls.title",
        hintKey: "v2.tasks.cloturerEls.hint",
        href: bienHref(b, "els"),
        ownerId: b.collaborateurSrs,
        daysWeight: 100,
      });
    }

    // dossierCloture=true mais dossierClos=false → finalisation à valider
    if (b.dossierCloture && !b.dossierClos) {
      out.push({
        id: `${b.id}:els-cloture-pending-final`,
        kind: "els-cloture-pending-final",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.validationFinale.title",
        hintKey: "v2.tasks.validationFinale.hint",
        href: bienHref(b, "els"),
        ownerId: b.collaborateurSrs,
        daysWeight: 200,
      });
    }

    // Transfert élec : ELS passée mais transfert pas fait
    const dElsDone = daysUntil(b.edlDatePrevue);
    if (dElsDone !== null && dElsDone <= 0 && !b.transfertElecFait && b.edlDatePrevue) {
      out.push({
        id: `${b.id}:transfert-elec-todo`,
        kind: "transfert-elec-todo",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.transfertElec.title",
        hintKey: "v2.tasks.transfertElec.hint",
        href: bienHref(b, "els"),
        ownerId: b.collaborateurSrs,
        daysWeight: 50,
      });
    }

    // appartPretPhotos toujours null après ELS
    if (dElsDone !== null && dElsDone <= 0 && b.appartPretPhotos == null && b.edlDatePrevue) {
      out.push({
        id: `${b.id}:photos-pret-todo`,
        kind: "photos-pret-todo",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.pretPhotos.title",
        hintKey: "v2.tasks.pretPhotos.hint",
        href: bienHref(b, "photos"),
        ownerId: b.collaborateurSrs,
        daysWeight: 60,
      });
    }
  }
  return out;
}

function derivePublicationTasks(biens: BienRow[]): Task[] {
  const out: Task[] = [];
  // Idem collab : la publication ne voit que les biens débloqués par CLevel
  const ready = biens.filter((b) => isVisibleForRole(b, "publication"));
  for (const b of ready) {
    if (!isActive(b)) continue;

    const photosOk = b.photosOk === "ok";
    const videoOk = b.videoOk === "ok";

    // Photos manquantes mais bien prêt
    if (b.appartPretPhotos === true && !photosOk) {
      out.push({
        id: `${b.id}:publication-photos-missing`,
        kind: "publication-photos-missing",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.photos.title",
        hintKey: "v2.tasks.photos.hint",
        href: bienHref(b, "publication"),
        daysWeight: 10,
      });
    }

    // Vidéo manquante
    if (photosOk && !videoOk) {
      out.push({
        id: `${b.id}:publication-video-missing`,
        kind: "publication-video-missing",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.video.title",
        hintKey: "v2.tasks.video.hint",
        href: bienHref(b, "publication"),
        daysWeight: 20,
      });
    }

    // Textes manquants
    const hasTexte =
      (b.descriptionFr || "").length > 20 ||
      (b.descriptionNl || "").length > 20 ||
      (b.descriptionEn || "").length > 20;
    if (photosOk && !hasTexte) {
      out.push({
        id: `${b.id}:publication-texte-missing`,
        kind: "publication-texte-missing",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.textes.title",
        hintKey: "v2.tasks.textes.hint",
        href: bienHref(b, "textes"),
        daysWeight: 25,
      });
    }

    // Modifs non publiées — staleness sur le CONTENU PORTAIL (helper canonique,
    // compare content_updated_at et non updated_at). Garde la condition
    // `last_published_at > 0` (un bien jamais publié explicitement ne génère pas
    // de tâche "repousser").
    if (b.statutPublication === "Publié" && b.last_published_at && hasUnpublishedChanges(b as Parameters<typeof hasUnpublishedChanges>[0])) {
      out.push({
        id: `${b.id}:publication-stale`,
        kind: "publication-stale",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.modifsNonPubliees.title",
        hintKey: "v2.tasks.modifsNonPubliees.hint",
        href: bienHref(b, "publication"),
        daysWeight: 30,
      });
    }

    // Bien prêt à publier (tout OK mais pas encore publié)
    if (
      photosOk &&
      hasTexte &&
      b.statutPublication !== "Publié" &&
      isDispo(b)
    ) {
      out.push({
        id: `${b.id}:publication-ready-to-push`,
        kind: "publication-ready-to-push",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.pretPublier.title",
        hintKey: "v2.tasks.pretPublier.hint",
        href: bienHref(b, "publication"),
        daysWeight: 5,
      });
    }
  }
  return out;
}

function deriveCLevelTasks(biens: BienRow[]): Task[] {
  const out: Task[] = [];
  for (const b of biens) {
    // PRIORITÉ HAUTE — biens bloqués par champs CLevel manquants. Tant que
    // CLevel n'a pas rempli les champs critiques, le bien n'apparait pas
    // chez le collab → blocage du pipeline.
    const clevel = isCLevelReady(b);
    if (!clevel.ok) {
      out.push({
        id: `${b.id}:clevel-fields-missing`,
        kind: "clevel-fields-missing",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: clevel.missing.length >= 5 ? "urgent" : "high",
        titleKey: "v2.tasks.blocked.title",
        hintKey: "v2.tasks.blocked.hint",
        params: { count: clevel.missing.length },
        href: bienHref(b),
        daysWeight: 1,
      });
    }

    // Cycle ouvert mais incomplet (collaborateur si ELS lancé, expert si PEB
    // lancé) : second garde-fou, distinct de l'identité — le bien se publie
    // quand même, mais n'atteint ni le collab ni le prestataire tant que le
    // C-Level n'a pas désigné qui s'en occupe (plan « deux cycles », 2026-09-15).
    // Collaborateur = bloquant (draft_cycle) ; prestataire ELS / expert PEB =
    // non bloquants mais à compléter (décision 2026-09-15 soir) — même liste
    // que le chip « Cycle à compléter ».
    const cycle = isCycleReady(b);
    const draftB = b as unknown as Record<string, unknown>;
    const cycleTodo = getCycleTodoFields(draftB).filter((k) => {
      const v = draftB[k];
      return !(typeof v === "string" && v.trim().length > 0);
    });
    if (clevel.ok && (!cycle.ok || cycleTodo.length > 0)) {
      out.push({
        id: `${b.id}:cycle-fields-missing`,
        kind: "cycle-fields-missing",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: cycle.ok ? "medium" : "high",
        titleKey: "v2.tasks.cycleBlocked.title",
        hintKey: "v2.tasks.cycleBlocked.hint",
        params: { count: cycle.missing.length + cycleTodo.length },
        href: bienHref(b, "els"),
        daysWeight: 2,
      });
    }

    // Disponibilité longue
    const d = daysSinceDispo(b);
    if (d !== null) {
      if (d >= 180) {
        out.push({
          id: `${b.id}:dispo-long-6m`,
          kind: "dispo-long-6m",
          bien_id: b.id,
          reference: ref(b),
          adresse: adresse(b),
          commune: commune(b),
          priority: "urgent",
          titleKey: "v2.tasks.dispoDepuisMois.title",
          hintKey: "v2.tasks.dispoDepuisMois.hintStrategique",
          params: { months: Math.floor(d / 30) },
          href: bienHref(b),
          daysWeight: -d,
        });
      } else if (d >= 90) {
        out.push({
          id: `${b.id}:dispo-long-3m`,
          kind: "dispo-long-3m",
          bien_id: b.id,
          reference: ref(b),
          adresse: adresse(b),
          commune: commune(b),
          priority: "high",
          titleKey: "v2.tasks.dispoDepuisMois.title",
          hintKey: "v2.tasks.dispoDepuisMois.hintVacance",
          params: { months: Math.floor(d / 30) },
          href: bienHref(b),
          daysWeight: -d,
        });
      } else if (d >= 30) {
        out.push({
          id: `${b.id}:dispo-long-1m`,
          kind: "dispo-long-1m",
          bien_id: b.id,
          reference: ref(b),
          adresse: adresse(b),
          commune: commune(b),
          priority: "medium",
          titleKey: "v2.tasks.dispoDepuisJours.title",
          params: { days: d },
          href: bienHref(b),
          daysWeight: -d,
        });
      }
    }

    // PEB absent — uniquement sur biens À LOUER (DISPONIBLE) pour ne pas
    // générer de fausse alerte sur les biens encore en cours de complétion.
    if (isDispo(b) && !b.classePeb) {
      out.push({
        id: `${b.id}:peb-absent-active`,
        kind: "peb-absent-active",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "high",
        titleKey: "v2.tasks.pebAbsent.title",
        hintKey: "v2.tasks.pebAbsent.hint",
        href: bienHref(b, "energie"),
        daysWeight: 40,
      });
    }

    // PEB expirant — idem, biens à louer uniquement. Garde `classePeb` : le cas
    // "classe absente" est déjà couvert par la tâche peb-absent-active ci-dessus
    // (sinon double tâche). Le source unique signale aussi "classe sans date".
    if (isDispo(b) && b.classePeb && isPebExpiringSoon(b.pebFinLe, b.classePeb)) {
      out.push({
        id: `${b.id}:peb-expiring`,
        kind: "peb-expiring",
        bien_id: b.id,
        reference: ref(b),
        adresse: adresse(b),
        commune: commune(b),
        priority: "medium",
        titleKey: "v2.tasks.pebExpire.title",
        hintRaw: b.pebFinLe ?? undefined,
        href: bienHref(b, "energie"),
        daysWeight: 70,
      });
    }

    // Mailing incomplet sur biens actifs (dispo, non loués/vendus).
    // En V2, plus de distinction EN PRÉPARATION — on alerte dès qu'un bien
    // actif a un mailing incomplet.
    if (isActive(b) && clevel.ok) {
      const m = getMailingCompleteness(b);
      if (!m.complete) {
        out.push({
          id: `${b.id}:mailing-incomplete-active`,
          kind: "mailing-incomplete-active",
          bien_id: b.id,
          reference: ref(b),
          adresse: adresse(b),
          commune: commune(b),
          priority: "medium",
          titleKey: "v2.tasks.mailingIncomplete.title",
          hintKey: "v2.tasks.mailingIncomplete.hint",
          params: { count: m.missing.length },
          href: bienHref(b),
          daysWeight: 80,
        });
      }
    }
  }
  return out;
}

function derivePrestataireTasks(biens: BienRow[], ctx: DeriveCtx): Task[] {
  const out: Task[] = [];
  const pid = ctx.prestataireId;

  for (const b of biens) {
    // Missions ELS attribuées : l'assignation active est `prestataireEle`
    // ("Prestataire assigné"). `prestataireEls` = trace pure (modèle 2026-06-06).
    if (pid && b.prestataireEle === pid && b.edlDatePrevue) {
      const d = daysUntil(b.edlDatePrevue);
      if (d !== null && d >= 0) {
        if (d === 0) {
          out.push({
            id: `${b.id}:mission-els-today`,
            kind: "mission-els-today",
            bien_id: b.id,
            reference: ref(b),
            adresse: adresse(b),
            commune: commune(b),
            priority: "urgent",
            titleKey: "v2.tasks.elsAujourdhui.title",
            hintRaw: b.edlHeure ?? undefined,
            href: `/v2/missions`,
            daysWeight: 0,
          });
        } else if (d <= 7) {
          out.push({
            id: `${b.id}:mission-els-soon`,
            kind: "mission-els-soon",
            bien_id: b.id,
            reference: ref(b),
            adresse: adresse(b),
            commune: commune(b),
            priority: d <= 2 ? "high" : "medium",
            titleKey: "v2.tasks.elsDansJours.title",
            params: { days: d },
            hintRaw: b.edlHeure ?? undefined,
            href: `/v2/missions`,
            daysWeight: d,
          });
        }
      }
    }

    // Missions PEB (expert)
    if (pid && b.expertPeb === pid && b.pebDatePrevue) {
      const d = daysUntil(b.pebDatePrevue);
      if (d !== null && d >= 0) {
        if (d === 0) {
          out.push({
            id: `${b.id}:mission-peb-today`,
            kind: "mission-peb-today",
            bien_id: b.id,
            reference: ref(b),
            adresse: adresse(b),
            commune: commune(b),
            priority: "urgent",
            titleKey: "v2.tasks.auditPebAujourdhui.title",
            hintRaw: b.pebHeure ?? undefined,
            href: `/v2/missions`,
            daysWeight: 0,
          });
        } else if (d <= 14) {
          out.push({
            id: `${b.id}:mission-peb-soon`,
            kind: "mission-peb-soon",
            bien_id: b.id,
            reference: ref(b),
            adresse: adresse(b),
            commune: commune(b),
            priority: d <= 3 ? "high" : "medium",
            titleKey: "v2.tasks.auditPebDansJours.title",
            params: { days: d },
            hintRaw: b.pebHeure ?? undefined,
            href: `/v2/missions`,
            daysWeight: d,
          });
        }
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Dérive l'inbox de tâches pour un rôle donné.
 * Tri : priorité puis daysWeight (urgent + jour proche en haut).
 */
export function deriveTasks(
  role: AppRole,
  biens: BienRow[],
  ctx: DeriveCtx = {},
): Task[] {
  let tasks: Task[] = [];
  switch (role) {
    case "collaborateur":
    case "collaborateur_els":
    case "collaborateur_afr":
      tasks = deriveCollabTasks(biens, ctx);
      break;
    case "publication":
      tasks = derivePublicationTasks(biens);
      break;
    case "c_level":
      tasks = deriveCLevelTasks(biens);
      break;
    case "prestataire":
      tasks = derivePrestataireTasks(biens, ctx);
      break;
    case "admin":
      // Admin voit l'union de toutes les tâches (mais hors prestataire qui dépend du pid)
      tasks = [
        ...deriveCollabTasks(biens, ctx),
        ...derivePublicationTasks(biens),
        ...deriveCLevelTasks(biens),
      ];
      break;
    default:
      tasks = [];
  }

  return tasks.sort((a, b) => {
    const p = PRIO_WEIGHT[a.priority] - PRIO_WEIGHT[b.priority];
    if (p !== 0) return p;
    return a.daysWeight - b.daysWeight;
  });
}

/**
 * Regroupe les tâches par catégorie thématique pour l'affichage de l'inbox.
 */
export interface TaskGroup {
  key: string;
  /** Clé i18n du libellé du groupe (à résoudre via `t(labelKey)` côté composant). */
  labelKey: string;
  tasks: Task[];
}

export function groupTasks(tasks: Task[]): TaskGroup[] {
  const groups: Record<string, TaskGroup> = {};
  const ensure = (key: string, labelKey: string) => {
    if (!groups[key]) groups[key] = { key, labelKey, tasks: [] };
    return groups[key];
  };

  for (const t of tasks) {
    let key = "other";
    let labelKey = "v2.tasks.groups.autres";
    if (t.kind === "clevel-fields-missing" || t.kind === "cycle-fields-missing") {
      key = "blockers";
      labelKey = "v2.tasks.groups.clevel";
    } else if (t.kind.startsWith("els-") || t.kind.startsWith("transfert-") || t.kind.startsWith("photos-pret")) {
      key = "els";
      labelKey = "v2.tasks.groups.pipelineEls";
    } else if (t.kind.startsWith("publication-")) {
      key = "publication";
      labelKey = "v2.tasks.groups.publication";
    } else if (t.kind.startsWith("dispo-") || t.kind.startsWith("mailing-")) {
      key = "ceo";
      labelKey = "v2.tasks.groups.vacance";
    } else if (t.kind.startsWith("peb-")) {
      key = "peb";
      labelKey = "v2.tasks.groups.energiePeb";
    } else if (t.kind.startsWith("mission-")) {
      key = "missions";
      labelKey = "v2.tasks.groups.missions";
    }
    ensure(key, labelKey).tasks.push(t);
  }

  // Ordre d'affichage des groupes — bloqueurs CLevel en tout premier
  const order = ["blockers", "missions", "els", "publication", "ceo", "peb", "other"];
  return order
    .map((k) => groups[k])
    .filter(Boolean);
}
